import dj from 'dayjs';
import Decimal from 'decimal.js';
import { z, type IssueData, type RefinementCtx } from 'zod';

export const date = () => z.coerce.date();
export type date = Date;

export const nullableDate = () => z.coerce.date().nullable();
export type nullableDate = Date | null;

export const any = () => z.any();

export const decimal = (): z.Schema<Decimal, z.ZodTypeDef, number | string | Decimal> => {
    return z
        .string()
        .or(z.number())
        .or(z.instanceof(Decimal))
        .transform((v, ctx) => {
            if (v == null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'A valid decimal number is required.',
                });
                return z.NEVER;
            }
            if (v === '') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Empty text is not a valid decimal number.',
                });
                return z.NEVER;
            }
            try {
                if (typeof v === 'number' || typeof v === 'string') {
                    return new Decimal(v);
                }
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid decimal number.',
                });
                return z.NEVER;
            } catch (e) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid decimal number.',
                });
                return z.NEVER;
            }
        })
        .or(z.instanceof(Decimal));
};
export type decimal = z.infer<ReturnType<typeof decimal>>;

export const nullableDecimal = (): z.Schema<Decimal | null, z.ZodTypeDef, null | number | string | Decimal> => {
    return z
        .string()
        .or(z.number())
        .or(z.instanceof(Decimal))
        .nullable()
        .transform((v, ctx) => {
            if (v == null) {
                return null;
            }
            if (v === '') {
                return null;
            }
            try {
                if (typeof v === 'number' || typeof v === 'string') {
                    return new Decimal(v);
                }
                return null;
            } catch (e) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid decimal number.',
                });
                return z.NEVER;
            }
        })
        .or(z.instanceof(Decimal));
};
export type nullableDecimal = z.infer<ReturnType<typeof nullableDecimal>>;

export const nullEmptyString = () =>
    z
        .string()
        .nullable()
        .transform(v => (v === undefined || v?.trim() === '' ? null : v));

export type nullEmptyString = string | null;

export const dayjs = () =>
    z.any().transform<dj.Dayjs>((val: any, ctx) => {
        if (dj.isDayjs(val)) {
            if (!val.isValid()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid dayjs object.',
                });
                return z.NEVER;
            }
            return val.clone();
        }
        if (val instanceof Date) {
            const result = dj(val);
            if (!result.isValid()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid date object.',
                });
                return z.NEVER;
            }
            return result;
        }
        if (typeof val === 'string') {
            const result = dj(val);
            if (!result.isValid()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid date format.',
                });
                return z.NEVER;
            }
            return result;
        }
        if (typeof val === 'number') {
            const result = dj(new Date(val * 1000));
            if (!result.isValid()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid unix timestamp.',
                });
                return z.NEVER;
            }
            return result;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Expected a date string or a number representing a unix timestamp.',
            params: { value: val },
        });
        return z.NEVER;
    });

export type dayjs = dj.Dayjs;

export const v_phone = (phone: string, issues: RefinementCtx) => {
    const parsed = phone.replace(/[\s|\-|(|)]/g, '');
    if (!parsed.match(/^\+\d{8,17}$/)) {
        issues.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Phone number must be in international format, e.g. +619998877766',
        });
        return z.NEVER;
    }
    return parsed;
};

export const v_password = (password: string, ctx: RefinementCtx) => {
    const issues: IssueData[] = [];
    if (password.length < 8) {
        issues.push({
            code: z.ZodIssueCode.custom,
            message: 'Password must be at least 8 characters long',
        });
    }
    if (password.length > 20) {
        issues.push({
            code: z.ZodIssueCode.custom,
            message: 'Password can be 20 characters long at most',
        });
    }
    const special = '!@#$%^&*(),.';
    if (password.match(/^[a-zA-Z0-9!@#$%^&*(),.]*$/) == null) {
        issues.push({
            code: z.ZodIssueCode.custom,
            message: `Password can only contain alphanumeric (a-Z 0-9) and ${special} special characters`,
        });
    }
    if (password.match(/[!@#$%^&*(),.]+/) == null) {
        issues.push({
            code: z.ZodIssueCode.custom,
            message: `Password must contain at least one special character ${special}`,
        });
    }
    for (const issue of issues.reverse()) {
        //reverse it because zod picks the first added issue for the error message
        ctx.addIssue(issue);
    }
    return password;
};
